import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from 'app/components/ui/card';
import { Mail, Eye, EyeOff, AlertCircle, Save, RefreshCw } from 'lucide-react';
import { useProfileSlice } from '../slice';
import { toast, useToast } from 'app/components/ui/use-toast';

const changeEmailSchema = z.object({
  current_email: z.string().email('Invalid email address'),
  new_email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required for email change'),
});

type ChangeEmailForm = z.infer<typeof changeEmailSchema>;

const defaultEmailValues: ChangeEmailForm = {
  current_email: '',
  new_email: '',
  password: '',
};

export function EmailTab() {
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const form = useForm<ChangeEmailForm>({
    resolver: zodResolver(changeEmailSchema),
    defaultValues: defaultEmailValues,
  });

  const { useChangeEmailMutation } = useProfileSlice();
  const [
    changeEmail,
    {
      isLoading: isChangingEmail,
      isError: isChangeEmailError,
      error: changeEmailError,
      data: changeEmailData,
    },
  ] = useChangeEmailMutation();

  const onSubmit = (data: ChangeEmailForm) => {
    changeEmail(data);
  };

  useEffect(() => {
    if (isChangeEmailError) {
      toast({
        description: (changeEmailError as any).data?.error,
        variant: 'destructive',
      });
    }
  }, [isChangeEmailError, changeEmailError]);

  useEffect(() => {
    if (changeEmailData) {
      toast({
        description: changeEmailData.message,
        variant: 'success',
      });
    }
  }, [changeEmailData]);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Change Email Address
          </CardTitle>
          <CardDescription>
            Update your email address. You'll need to confirm the new email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_email">Current Email</Label>
            <Input
              id="current_email"
              type="email"
              {...form.register('current_email')}
              placeholder="Enter your current email"
            />
            {form.formState.errors.current_email && (
              <p className="text-xs text-red-500">
                {form.formState.errors.current_email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="new_email">New Email Address</Label>
            <Input
              id="new_email"
              type="email"
              {...form.register('new_email')}
              placeholder="Enter your new email address"
            />
            {form.formState.errors.new_email && (
              <p className="text-xs text-red-500">
                {form.formState.errors.new_email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email_password">Current Password</Label>
            <div className="relative">
              <Input
                id="email_password"
                type={showEmailPassword ? 'text' : 'password'}
                {...form.register('password')}
                placeholder="Enter your password to confirm"
              />
              <button
                type="button"
                onClick={() => setShowEmailPassword(v => !v)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showEmailPassword ? (
                  <EyeOff className="w-4 h-4 text-gray-500" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-500" />
                )}
              </button>
            </div>
            {form.formState.errors.password && (
              <p className="text-xs text-red-500">
                {form.formState.errors.password.message}
              </p>
            )}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm text-yellow-800 font-medium">
                  Email Change Process
                </p>
                <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                  <li>
                    • You'll receive a confirmation email at the new address
                  </li>
                  <li>• Click the confirmation link to verify the new email</li>
                  <li>• Your email will be updated after confirmation</li>
                  <li>• You can cancel the change within 24 hours</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Request Email Change
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
